import React from 'react';
import {
  AgnesBadge,
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  AgnesPageHeader,
} from '@agnes/ui';
import { KeyRound, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { usePerfisStore } from '@/stores/usePerfisStore';
import { usePermissionsStore } from '@/stores/usePermissionsStore';
import { confirm } from '@/stores/useConfirm';
import { SearchInput } from '@/components/SearchInput';
import PerfilFormModal from '@/components/admin/PerfilFormModal';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

type Perfil = any;

export default function PagePerfisPermissao() {
  const [busca, setBusca] = React.useState('');
  const [modalAberto, setModalAberto] = React.useState(false);
  const [editando, setEditando] = React.useState<Perfil | null>(null);
  const lista = usePerfisStore((s) => s.lista);
  const carregando = usePerfisStore((s) => s.carregando);
  const podeEscrever = usePermissionsStore((s) => s.hasPermission('egov:config.perfis:write'));

  React.useEffect(() => {
    usePerfisStore.getState().buscar();
  }, []);

  const nome = (r: Perfil) => r.display_name ?? r.name ?? '';
  const data = lista.filter((p) => nome(p).toLowerCase().includes(busca.toLowerCase()));

  function abrirNovo() {
    setEditando(null);
    setModalAberto(true);
  }
  function abrirEdicao(r: Perfil) {
    setEditando(r);
    setModalAberto(true);
  }

  async function excluir(r: Perfil) {
    const ok = await confirm({
      title: 'Excluir perfil',
      message: `Excluir o perfil "${nome(r)}"? Esta ação não pode ser desfeita.`,
      okLabel: 'Excluir',
    });
    if (ok) await usePerfisStore.getState().excluir(r.id);
  }

  const columns = [
    {
      key: 'name',
      header: 'Perfil',
      width: 240,
      render: (r: Perfil) => (
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-agnes-primary" />
          {nome(r)}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Descrição',
      width: 360,
      render: (r: Perfil) => r.description ?? '-',
    },
    {
      key: 'permissions',
      header: 'Permissões',
      width: 160,
      render: (r: Perfil) => <AgnesBadge>{r.permissions?.length ?? 0} permissões</AgnesBadge>,
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<KeyRound />}
        title="Perfis de Permissão"
        subtitle="Defina os níveis de acesso e permissões granulares"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Administração' },
          { label: 'Perfis de Permissão' },
        ]}
        actions={
          podeEscrever ? (
            <AgnesButton
              variant="primary"
              startIcon={<Plus className="h-4 w-4" />}
              onClick={abrirNovo}
            >
              Novo perfil
            </AgnesButton>
          ) : undefined
        }
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <div className="mb-4 max-w-sm">
            <SearchInput
              value={busca}
              onValueChange={setBusca}
              onSearch={() => {}}
              placeholder="Buscar por perfil"
            />
          </div>

          <TableWrapper>
            <AgnesDataTable<Perfil>
              columns={columns}
              data={data}
              rowKey={(r) => String(r.id)}
              loading={carregando}
              actions={(r: Perfil) => (
                <span className="flex items-center gap-1">
                  <AgnesButton
                    variant="ghost"
                    size="sm"
                    title={podeEscrever ? 'Editar permissões' : 'Ver permissões'}
                    onClick={() => abrirEdicao(r)}
                  >
                    <Pencil className="h-4 w-4" />
                  </AgnesButton>
                  {podeEscrever && !r.is_system && (
                    <AgnesButton
                      variant="ghost"
                      size="sm"
                      title="Excluir perfil"
                      onClick={() => excluir(r)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </AgnesButton>
                  )}
                </span>
              )}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>

      <PerfilFormModal open={modalAberto} onClose={() => setModalAberto(false)} perfil={editando} />
    </>
  );
}
